import React, { useEffect, useRef, useState } from "react";
import { useEngagement } from "@/contexts/EngagementContext";
import { useAuth } from "@/contexts/AuthContext";
import { API_URL } from "@/lib/api";
import { Users, Wifi, WifiOff, ChevronUp } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { engagementName } from "@/lib/engagementLabel";   // Batch 15 · N5 (F-119)
import { useSaveBarShown } from "@/lib/saveBarPresence";   // Batch 29 · M70

/**
 * Bottom-right dock showing who else on my tenant is looking at THIS engagement
 * right now. WebSocket-driven. Silent no-op when there is no active engagement.
 */
export default function PresenceDock() {
  const { user } = useAuth();
  const { lang, dict, t } = useI18n();
  const { currentEngId, current } = useEngagement();
  const [roster, setRoster] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const barShown = useSaveBarShown();
  const pingRef = useRef(null);

  const signedIn = !!user, userId = user?.id;   // Step 7c · E3: the two parts of user the socket reads
  useEffect(() => {
    if (!currentEngId || !signedIn) return;
    const token = localStorage.getItem("af_token");
    if (!token) return;
    const wsUrl = API_URL
      .replace(/^http/, "ws")
      .replace(/\/api$/, "") + `/api/ws/presence/${currentEngId}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch { return; }
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      pingRef.current = setInterval(() => {
        try { ws.readyState === 1 && ws.send("ping"); } catch { /* ignore */ }
      }, 20000);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "presence" && msg.engagement_id === currentEngId) {
          setRoster(Array.isArray(msg.roster) ? msg.roster : []);
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => { setConnected(false); setRoster([]); };
    ws.onerror = () => { setConnected(false); };
    return () => {
      if (pingRef.current) clearInterval(pingRef.current);
      pingRef.current = null;
      try { ws.close(); } catch { /* ignore */ }
      setConnected(false); setRoster([]);
    };
  }, [currentEngId, signedIn, userId]);

  if (!currentEngId || !user) return null;

  const others = roster.filter((r) => r.user_id !== user.id);
  const label = others.length === 0
    ? t("presence.onlyOne")
    : `${fmtPlural(others.length, lang, dict.plurals.others)} ${t("presence.viewing")}`;

  return (
    // Batch 29 · M70: above a Save / Cancel bar (they stick to the page bottom), so the bar's Save button stays clear
    <div className={`fixed ${barShown ? "bottom-24" : "bottom-8"} end-4 z-40`} data-testid="presence-dock" data-raised={barShown ? "1" : "0"}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="group flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-lg transition hover:shadow-xl dark:border-slate-800 dark:bg-slate-900"
            data-testid="presence-dock-toggle"
            title={label}
          >
            <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
              {connected
                ? <Users className="h-3.5 w-3.5 text-emerald-600" />
                : <WifiOff className="h-3.5 w-3.5 text-slate-400" />}
              {connected && (
                <span className="absolute -top-0.5 -end-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900" />
              )}
            </span>
            <span className="hidden text-xs font-medium sm:inline"
                  data-testid="presence-dock-count">
              {others.length}
            </span>
            <ChevronUp className="h-3 w-3 text-slate-400 transition group-hover:text-slate-600" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-72 p-0"
                        data-testid="presence-dock-panel">
          <div className="border-b px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Wifi className="h-3 w-3 text-emerald-600" />
              {t("presence.live")} · {(current && engagementName(current, t)) || t("presence.engagement")}
            </div>
            <div className="text-sm font-medium">{label}</div>
          </div>
          {others.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground"
                 data-testid="presence-empty">
              {t("presence.empty")}
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {others.map((r) => (
                <li key={r.user_id}
                    className="flex items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0"
                    data-testid={`presence-user-${r.user_id}`}>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {(r.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium" title={r.name || ""}>{r.name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{r.role || ""}</div>
                  </div>
                  <span className="h-2 w-2 rounded-full bg-emerald-500" title={t("presence.online")} />
                </li>
              ))}
            </ul>
          )}
          <div className="border-t px-3 py-2 text-[10px] text-muted-foreground">
            {t("presence.tenantNote")}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
